import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import Loader from 'react-loader-spinner';
import { FiSave, FiTrash2, FiUserPlus, FiX } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import defaultPlayerImage from '../images/defaultAvatar.png';
import '../styles/leagueDisqualificationsManagement.css';

function LeagueDisqualificationsManagement() {
  const dispatch = useDispatch();

  const [championships, setChampionships] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);

  const [yellowAmount, setYellowAmount] = useState('4');
  const [savingYellowAmount, setSavingYellowAmount] = useState(false);

  const [leaguePlayers, setLeaguePlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [playerSearch, setPlayerSearch] = useState('');
  const [playersSearched, setPlayersSearched] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [disqualificationMode, setDisqualificationMode] = useState('matches');
  const [matchesAmount, setMatchesAmount] = useState('1');
  const [customToDate, setCustomToDate] = useState('');
  const [startsFrom, setStartsFrom] = useState('');
  const [reason, setReason] = useState('');
  const [savingPlayerDisqualification, setSavingPlayerDisqualification] = useState(false);

  const [playerDisqualifications, setPlayerDisqualifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const getAuthHeaders = async () => {
    const isTokenExpired = checkIfTokenExpired();
    if (isTokenExpired) {
      await dispatch(refreshTokens());
    }

    const tokens = getTokensFromStore();
    return {
      Authorization: 'Bearer ' + tokens.access_token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  };

  const extractList = (payload, keys = []) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (!payload || typeof payload !== 'object') {
      return [];
    }

    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (Array.isArray(payload[key])) {
        return payload[key];
      }
      if (payload[key] && Array.isArray(payload[key].data)) {
        return payload[key].data;
      }
    }

    if (Array.isArray(payload.data)) {
      return payload.data;
    }

    return [];
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, {
      keys: ['name'],
      threshold: 0.3,
    });

    return (value) => {
      if (!value.length) {
        return options;
      }
      return fuse.search(value).map((item) => item.item);
    };
  };

  const toDateYMD = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toDateTimeLocal = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const toApiDateTime = (value) => {
    if (!value) {
      return null;
    }

    return `${value}:00`;
  };

  // Срок считаем от даты начала дисквалификации, а не от сегодняшнего дня
  const getRelativeEndDate = (mode) => {
    const start = startsFrom ? new Date(startsFrom) : null;
    const now = start && !isNaN(start.getTime()) ? start : new Date();
    if (mode === 'month') {
      const next = new Date(now);
      next.setMonth(next.getMonth() + 1);
      return toDateYMD(next);
    }
    if (mode === 'two_months') {
      const next = new Date(now);
      next.setMonth(next.getMonth() + 2);
      return toDateYMD(next);
    }
    if (mode === 'three_months') {
      const next = new Date(now);
      next.setMonth(next.getMonth() + 3);
      return toDateYMD(next);
    }
    if (mode === 'half_year') {
      const next = new Date(now);
      next.setMonth(next.getMonth() + 6);
      return toDateYMD(next);
    }
    return null;
  };

  const getPlayerImage = (player) => {
    const raw = player?.player_image || player?.image;
    if (!raw || raw === '/images/others/defaultLogo.png') {
      return defaultPlayerImage;
    }
    if (String(raw).startsWith('http')) {
      return raw;
    }
    if (String(raw).startsWith('/')) {
      return BASE_URL.url + raw;
    }
    return `${BASE_URL.url}/${raw}`;
  };

  const formatDateRu = (value) => {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const formatDateTimeRu = (value) => {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };


  const loadChampionships = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(BASE_URL.url + '/leagues/1/adminLeagues', {}, { headers });
      const leaguesData = extractList(response.data);
      const championshipsMap = {};

      leaguesData.forEach((item) => {
        const championshipName = item.championat_name || 'Без чемпионата';

        if (!championshipsMap[championshipName]) {
          championshipsMap[championshipName] = {
            value: championshipName,
            name: championshipName,
            leagues: [],
          };
        }

        if (item.league_id > 0) {
          championshipsMap[championshipName].leagues.push({
            value: item.league_id,
            name: item.league_name,
            seasons: item.seasons || [],
            ...item,
          });
        }
      });

      setChampionships(
        Object.values(championshipsMap)
          .filter((item) => item.leagues.length > 0)
          .map((item, index) => ({ ...item, key: index }))
      );
    } catch (error) {
      console.error('Error loading championships:', error);
      setMessage({ text: 'Не удалось загрузить список лиг', type: 'error' });
    }
  };

  const loadYellowAmount = async (leagueId, seasonId) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      BASE_URL.url + `/leagues/${leagueId}/disqualification_info`,
      { season_id: seasonId },
      { headers }
    );

    const body = response.data?.data || response.data || {};
    const amount = body.yellow_amount;
    setYellowAmount(String(amount || 0));
  };

  const loadLeaguePlayers = async (leagueId, seasonId, search = '') => {
    setLoadingPlayers(true);
    const headers = await getAuthHeaders();
    try {
      const searchValue = search.trim();
      const searchParts = searchValue.split(' ').filter((part) => part.length > 0);
      const response = await axios.post(
        BASE_URL.url + '/players/get_league_players',
        {
          league_id: leagueId,
          season_id: seasonId,
          search: searchValue,
          family: searchParts[0] || undefined,
          name: searchParts[1] || undefined,
        },
        { headers }
      );

      const players = extractList(response.data, ['players']).map((item, index) => ({
        ...item,
        value: item.player_id || item.id,
        key: index,
        name: `${item.family || ''} ${item.name || ''}`.trim() || `Игрок #${item.player_id || item.id}`,
      }));

      setLeaguePlayers(players);
      setPlayersSearched(true);
    } catch (error) {
      setLeaguePlayers([]);
      setPlayersSearched(true);
      throw error;
    } finally {
      setLoadingPlayers(false);
    }
  };

  const loadPlayerDisqualifications = async (leagueId, seasonId) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      BASE_URL.url + `/leagues/${leagueId}/get_disqualification_player`,
      { season_id: seasonId },
      { headers }
    );

    setPlayerDisqualifications(extractList(response.data, ['players', 'items', 'disqualifications']));
  };

  const loadPageData = async (leagueId, seasonId) => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      await Promise.all([
        loadYellowAmount(leagueId, seasonId),
        loadPlayerDisqualifications(leagueId, seasonId),
      ]);
    } catch (error) {
      console.error('Error loading disqualification page:', error);
      setMessage({ text: 'Не удалось загрузить данные дисквалификаций', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChampionships();
  }, []);

  useEffect(() => {
    setStartsFrom(toDateTimeLocal(new Date()));
  }, []);

  const handleChampionshipChange = (value) => {
    const championship = championships.find((item) => item.value === value) || null;
    setSelectedChampionship(championship);
    setLeagues(championship ? championship.leagues : []);

    setSelectedLeague(null);
    setSeasons([]);
    setSelectedSeason(null);
    setLeaguePlayers([]);
    setPlayersSearched(false);
    setPickerOpen(false);
    setSelectedPlayer(null);
    setPlayerDisqualifications([]);
  };

  const handleLeagueChange = (value) => {
    const league = leagues.find((item) => String(item.value) === String(value)) || null;
    setSelectedLeague(league);

    const preparedSeasons = (league?.seasons || []).map((season, index) => ({
      ...season,
      value: season.season_id || season.id,
      name: season.season_name || season.name || `Сезон ${index + 1}`,
      key: index,
    }));

    setSeasons(preparedSeasons);
    setSelectedSeason(null);
    setLeaguePlayers([]);
    setSelectedPlayer(null);
    setPlayerDisqualifications([]);
  };

  const handleSeasonChange = async (value) => {
    const season = seasons.find((item) => String(item.value) === String(value)) || null;
    setSelectedSeason(season);
    setSelectedPlayer(null);
    setLeaguePlayers([]);
    setPlayersSearched(false);

    if (!selectedLeague || !season) {
      return;
    }

    await loadPageData(selectedLeague.value, season.value);
  };

  const handleSaveYellowAmount = async () => {
    if (!selectedLeague || !selectedSeason) {
      return;
    }

    setSavingYellowAmount(true);
    setMessage({ text: '', type: '' });
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/add_disqualification`,
        {
          season_id: selectedSeason.value,
          yellow_cards_amount: Number(yellowAmount || 0),
        },
        { headers }
      );

      setMessage({ text: 'Порог по желтым карточкам сохранен', type: 'success' });
    } catch (error) {
      console.error('Error saving yellow disqualification amount:', error);
      setMessage({ text: 'Не удалось сохранить порог дисквалификации', type: 'error' });
    } finally {
      setSavingYellowAmount(false);
    }
  };

  const durationOptions = useMemo(() => ([
    { value: 'matches', name: 'По количеству матчей' },
    { value: 'month', name: 'До даты: 1 месяц' },
    { value: 'two_months', name: 'До даты: 2 месяца' },
    { value: 'three_months', name: 'До даты: 3 месяца' },
    { value: 'half_year', name: 'До даты: 6 месяцев' },
    { value: 'custom_date', name: 'До определенной даты' },
  ]), []);

  const openPlayerPicker = () => {
    setPickerOpen(true);
    setPlayerSearch('');
    setLeaguePlayers([]);
    setPlayersSearched(false);
  };

  const closePlayerPicker = () => {
    setPickerOpen(false);
    setPlayerSearch('');
    setLeaguePlayers([]);
    setPlayersSearched(false);
  };

  const handleSearchPlayers = async () => {
    if (!selectedLeague || !selectedSeason) {
      return;
    }

    try {
      await loadLeaguePlayers(selectedLeague.value, selectedSeason.value, playerSearch);
    } catch (error) {
      console.error('Error loading league players:', error);
      setMessage({ text: 'Не удалось загрузить игроков лиги', type: 'error' });
    }
  };

  const handleSelectPlayer = (player) => {
    setSelectedPlayer(player);
    closePlayerPicker();
  };

  const handleAddPlayerDisqualification = async () => {
    if (!selectedLeague || !selectedSeason || !selectedPlayer) {
      return;
    }

    let preparedMatchesAmount = null;
    let preparedToDate = null;

    if (disqualificationMode === 'matches') {
      preparedMatchesAmount = Number(matchesAmount || 0);
      if (!Number.isInteger(preparedMatchesAmount) || preparedMatchesAmount < 1) {
        setMessage({ text: 'Укажите корректное количество матчей', type: 'error' });
        return;
      }
    }

    if (
      disqualificationMode === 'month'
      || disqualificationMode === 'two_months'
      || disqualificationMode === 'three_months'
      || disqualificationMode === 'half_year'
    ) {
      preparedToDate = getRelativeEndDate(disqualificationMode);
    }

    if (disqualificationMode === 'custom_date') {
      if (!customToDate) {
        setMessage({ text: 'Выберите дату окончания дисквалификации', type: 'error' });
        return;
      }
      preparedToDate = customToDate;
    }

    if (preparedToDate && startsFrom && preparedToDate < String(startsFrom).slice(0, 10)) {
      setMessage({ text: 'Дата окончания раньше даты начала дисквалификации', type: 'error' });
      return;
    }

    setSavingPlayerDisqualification(true);
    setMessage({ text: '', type: '' });
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/add_disqualification_player`,
        {
          season_id: selectedSeason.value,
          player_id: selectedPlayer.value,
          matches_amount: preparedMatchesAmount,
          to_date: preparedToDate,
          from_date: toApiDateTime(startsFrom),
          reason: reason.trim() || null,
        },
        { headers }
      );

      await loadPlayerDisqualifications(selectedLeague.value, selectedSeason.value);
      setMessage({ text: 'Индивидуальная дисквалификация добавлена', type: 'success' });
      setSelectedPlayer(null);
      setMatchesAmount('1');
      setCustomToDate('');
      setStartsFrom(toDateTimeLocal(new Date()));
      setReason('');
    } catch (error) {
      console.error('Error adding player disqualification:', error);
      setMessage({ text: 'Не удалось добавить индивидуальную дисквалификацию', type: 'error' });
    } finally {
      setSavingPlayerDisqualification(false);
    }
  };

  const handleRemovePlayerDisqualification = async (id) => {
    if (!id || !selectedLeague || !selectedSeason) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/remove_disqualification_player`,
        { id },
        { headers }
      );

      setPlayerDisqualifications((prev) => prev.filter((item) => String(item.id) !== String(id)));
      setMessage({ text: 'Дисквалификация удалена', type: 'success' });
    } catch (error) {
      console.error('Error removing player disqualification:', error);
      setMessage({ text: 'Не удалось удалить дисквалификацию', type: 'error' });
    }
  };

  return (
    <div className="layoutWrapper league-disqualification-page">
      <div className="matchCenterWrapper">
        <div className="matchCenterHeader league-disqualification-header">
          <span className="matchCenterHeaderTitle">Управление дисквалификациями</span>
        </div>

        <div className="league-disqualification-content">
          {message.text && (
            <div className={`league-disqualification-message ${message.type === 'success' ? 'success' : 'error'}`}>
              {message.text}
            </div>
          )}

          <div className="league-disqualification-filters">
            <div className="league-disqualification-filter-group">
              <label>Чемпионат</label>
              <SelectSearch
                options={championships}
                value={selectedChampionship?.value || ''}
                onChange={handleChampionshipChange}
                search
                filterOptions={fuzzySearch}
                placeholder="Выберите чемпионат"
              />
            </div>

            <div className="league-disqualification-filter-group">
              <label>Лига</label>
              <SelectSearch
                options={leagues}
                value={selectedLeague ? String(selectedLeague.value) : ''}
                onChange={handleLeagueChange}
                search
                filterOptions={fuzzySearch}
                placeholder="Выберите лигу"
                disabled={!selectedChampionship}
              />
            </div>

            <div className="league-disqualification-filter-group">
              <label>Сезон</label>
              <SelectSearch
                options={seasons}
                value={selectedSeason ? String(selectedSeason.value) : ''}
                onChange={handleSeasonChange}
                search
                filterOptions={fuzzySearch}
                placeholder="Выберите сезон"
                disabled={!selectedLeague}
              />
            </div>
          </div>

          {!selectedLeague || !selectedSeason ? (
            <div className="league-disqualification-empty">Выберите лигу и сезон для настройки дисквалификаций</div>
          ) : loading ? (
            <div className="league-disqualification-loading">
              <Loader type="TailSpin" color="#3182ce" height={36} width={36} />
            </div>
          ) : (
            <>
              <div className="league-disqualification-section">
                <div className="league-disqualification-section-header">
                  <h3>Дисквалификация по желтым карточкам</h3>
                  <button
                    className="league-disqualification-save-btn"
                    onClick={handleSaveYellowAmount}
                    disabled={savingYellowAmount}
                  >
                    <FiSave /> {savingYellowAmount ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>

                <div className="league-disqualification-row">
                  <label>Количество желтых карточек</label>
                  <input
                    type="number"
                    min="1"
                    value={yellowAmount}
                    onChange={(e) => setYellowAmount(e.target.value)}
                  />
                </div>
              </div>

              <div className="league-disqualification-section">
                <div className="league-disqualification-section-header">
                  <h3>Индивидуальная дисквалификация игрока</h3>
                </div>

                <div className="league-disqualification-grid">
                  <div className="league-disqualification-field">
                    <label>Игрок</label>
                    <button
                      type="button"
                      className="league-disqualification-player-picker-btn"
                      onClick={openPlayerPicker}
                      disabled={!selectedLeague || !selectedSeason}
                    >
                      {selectedPlayer
                        ? `${selectedPlayer.family || ''} ${selectedPlayer.name || ''}`.trim()
                        : 'Выбрать игрока'}
                    </button>
                  </div>

                  <div className="league-disqualification-field">
                    <label>Тип ограничения</label>
                    <SelectSearch
                      options={durationOptions}
                      value={disqualificationMode}
                      onChange={setDisqualificationMode}
                      search
                      filterOptions={fuzzySearch}
                      placeholder="Выберите тип"
                    />
                  </div>

                  {disqualificationMode === 'matches' && (
                    <div className="league-disqualification-field">
                      <label>Количество матчей</label>
                      <input
                        type="number"
                        min="1"
                        value={matchesAmount}
                        onChange={(e) => setMatchesAmount(e.target.value)}
                      />
                    </div>
                  )}

                  {disqualificationMode === 'custom_date' && (
                    <div className="league-disqualification-field">
                      <label>Дата окончания</label>
                      <input
                        type="date"
                        value={customToDate}
                        onChange={(e) => setCustomToDate(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="league-disqualification-field">
                    <label>Начиная с</label>
                    <input
                      type="datetime-local"
                      value={startsFrom}
                      onChange={(e) => setStartsFrom(e.target.value)}
                    />
                  </div>

                  <div className="league-disqualification-field">
                    <label>Причина</label>
                    <input
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Например: грубая игра"
                    />
                  </div>
                </div>

                <button
                  className="league-disqualification-add-btn"
                  onClick={handleAddPlayerDisqualification}
                  disabled={savingPlayerDisqualification || !selectedPlayer}
                >
                  <FiUserPlus /> {savingPlayerDisqualification ? 'Добавление...' : 'Добавить дисквалификацию'}
                </button>

                {playerDisqualifications.length > 0 ? (
                  <div className="league-disqualification-list">
                    {playerDisqualifications.map((item) => (
                      <div key={item.id} className="league-disqualification-item">
                        <div className="league-disqualification-player">
                          <img src={getPlayerImage(item)} alt="Player" />
                          <div>
                            <div className="league-disqualification-player-name">{`${item.player_family || ''} ${item.player_name || ''}`.trim()}</div>
                            <div className="league-disqualification-player-meta">
                              {item.matches_amount ? `Матчи: ${item.matches_amount}` : `До даты: ${formatDateRu(item.to_date)}`}
                            </div>
                            {item.from_date ? (
                              <div className="league-disqualification-player-meta">
                                Начиная с: {formatDateTimeRu(item.from_date)}
                              </div>
                            ) : null}
                            {item.reason ? (
                              <div className="league-disqualification-player-meta">Причина: {item.reason}</div>
                            ) : null}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="league-disqualification-remove-btn"
                          onClick={() => handleRemovePlayerDisqualification(item.id)}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="league-disqualification-empty-list">Индивидуальные дисквалификации отсутствуют</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {pickerOpen && (
        <div className="league-disqualification-picker-overlay" onClick={closePlayerPicker}>
          <div className="league-disqualification-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="league-disqualification-picker-header">
              <h3>Выбор игрока</h3>
              <button type="button" className="league-disqualification-picker-close" onClick={closePlayerPicker}>
                <FiX />
              </button>
            </div>

            <div className="league-disqualification-picker-body">
              <div className="league-disqualification-picker-search-row">
                <input
                  type="text"
                  className="league-disqualification-picker-search"
                  placeholder="Поиск по фамилии"
                  value={playerSearch}
                  onChange={(e) => setPlayerSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchPlayers();
                    }
                  }}
                />
                <button
                  type="button"
                  className="league-disqualification-picker-search-btn"
                  onClick={handleSearchPlayers}
                  disabled={loadingPlayers}
                >
                  {loadingPlayers ? 'Поиск...' : 'Искать'}
                </button>
              </div>

              {loadingPlayers ? (
                <div className="league-disqualification-picker-loading">Загрузка игроков...</div>
              ) : (
                <div className="league-disqualification-picker-list">
                  {leaguePlayers.length > 0 ? (
                    leaguePlayers.map((player) => (
                      <button
                        type="button"
                        key={player.player_id || player.id}
                        className={`league-disqualification-picker-item ${selectedPlayer?.value === (player.player_id || player.id) ? 'selected' : ''}`}
                        onClick={() => handleSelectPlayer(player)}
                      >
                        <img
                          src={getPlayerImage(player)}
                          alt={`${player.family || ''} ${player.name || ''}`.trim()}
                          className="league-disqualification-picker-item-image"
                          onError={(e) => { e.target.src = defaultPlayerImage; }}
                        />
                        <span className="league-disqualification-picker-item-name">{`${player.name || ''}`.trim()}</span>
                        <span className="league-disqualification-picker-item-team">{player.team_name || 'Без команды'}</span>
                      </button>
                    ))
                  ) : playersSearched ? (
                    <div className="league-disqualification-picker-empty">Игроки не найдены</div>
                  ) : (
                    <div className="league-disqualification-picker-empty">Введите запрос и нажмите "Искать"</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeagueDisqualificationsManagement;
